// GenericQuizAssign.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  MenuItem,
  Select,
  TextField,
  Grid,
  Typography,
  Alert,
  Radio,
  FormControl,
  TablePagination,
  Snackbar,
  Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import MuiAlert from "@mui/material/Alert";
import SearchField from "react-search-field";

const GenericQuizAssign = ({ config }) => {
  // config with defaults
  const {
    labType,
    apiEndpoint,
    fieldMapping = {
      nameField: "name",
      startDateField: "start_date",
      endDateField: "end_date",
    },
    dataFilter = null,
    allowedStatuses = [
      "Creating",
      "Stopping",
      "Starting",
      "Started",
      "Cordoned",
      "Start Initiated",
      "Stopped",
      "Stop Initiated",
      "Running",
    ],
  } = config ?? {};

  // UI / data state
  const [pcclabdata, setPcclabdata] = useState([]);
  const [quizType, setQuizType] = useState([]);

  // form state
  const [selectedLabId, setSelectedLabId] = useState("");
  const [selectedAssessmentName, setSelectedAssessmentName] = useState("");
  const [assessmentDate, setAssessmentDate] = useState("");
  const [assignmentName, setAssignmentName] = useState("");
  const [assessmentType, setAssessmentType] = useState("common");

  // derived lab fields
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [stopTime, setStopTime] = useState("");
  const [traineeCount, setTraineeCount] = useState("");

  // UI helpers & error
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [paginationModel, setPaginationModel] = useState({ pageSize: 5, page: 0 });
  const [assignNameError, setAssignNameError] = useState("");
  const [quizAssignError, setQuizAssignError] = useState("");
  const [submitCompleted, setSubmitCompleted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  const vertical = "top";
  const horizontal = "center";

  // ---------- Helpers: fetch wrapper ----------
  const getAuthHeaders = useCallback(() => {
    return {
      "Content-Type": "application/json",
      Authorization: "Bearer " + localStorage.getItem("token"),
    };
  }, []);

  // ---------- Fetch lab data ----------
  useEffect(() => {
    let mounted = true;
    const loadLabData = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}${apiEndpoint}`, {
          method: "GET",
          headers: getAuthHeaders(),
        });
        const data = await res.json();
        if (!mounted) return;
        if (!data.detail) {
          const instance = Array.isArray(data)
            ? data.filter((item) => allowedStatuses.includes(item.status))
            : [];
          const filtered = dataFilter ? dataFilter(instance) : instance;
          setPcclabdata(filtered);

          if (filtered.length > 0) {
            const firstLab = filtered[0];
            setStartDate(firstLab[fieldMapping.startDateField] || "");
            setEndDate(firstLab[fieldMapping.endDateField] || "");
          } else {
            setStartDate("");
            setEndDate("");
          }
        } else {
          setPcclabdata([]);
        }
      } catch (err) {
        console.error("Load labs error:", err);
      }
    };

    loadLabData();
    // re-run only when apiEndpoint or submitCompleted toggles (so refresh after submit)
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiEndpoint, getAuthHeaders, dataFilter, allowedStatuses, fieldMapping, submitCompleted]);

  // ---------- Fetch active quizzes ----------
  useEffect(() => {
    let mounted = true;
    const loadQuizzes = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/quizdetails/`, {
          method: "GET",
          headers: getAuthHeaders(),
        });
        const data = await res.json();
        if (!mounted) return;
        if (!data.detail) {
          const activeQuizzes = (Array.isArray(data) ? data : []).filter(
            (item) => item.status === "Active"
          );
          setQuizType(
            activeQuizzes.map((item) => ({
              name: item.name,
              organization_type: item.organization_type,
            }))
          );
        } else {
          setQuizType([]);
        }
      } catch (err) {
        console.error("Load quizzes error:", err);
      }
    };

    loadQuizzes();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- Memoized filtered lists ----------
  const filteredPccData = useMemo(() => {
    const term = (searchTerm || "").toLowerCase();
    return pcclabdata.filter((instance) => {
      const nameValue = instance[fieldMapping.nameField] || "";
      return nameValue.toLowerCase().includes(term);
    });
  }, [pcclabdata, searchTerm, fieldMapping]);

  const paginatedPccData = useMemo(() => {
    return filteredPccData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredPccData, page, rowsPerPage]);

  const filteredQuizType = useMemo(() => {
    const term = (searchTerm || "").toLowerCase();
    return quizType.filter((quiz) => quiz.name.toLowerCase().includes(term));
  }, [quizType, searchTerm]);

  const filteredAssessments = useMemo(() => {
    return filteredQuizType.filter((assessment) => {
      if (assessmentType === "common") {
        return assessment.organization_type === "Common";
      }
      if (assessmentType === "individual") {
        return assessment.organization_type === "Individual";
      }
      return true;
    });
  }, [filteredQuizType, assessmentType]);

  // ---------- DataGrid columns (memoized) ----------
  const labNameColumn = useMemo(
    () => ({
      field: fieldMapping.nameField,
      headerName: "Lab Name",
      flex: 1,
    }),
    [fieldMapping]
  );

  const startDateColumn = useMemo(
    () => ({
      field: fieldMapping.startDateField,
      headerName: "Start Date",
      flex: 0.8,
    }),
    [fieldMapping]
  );

  const endDateColumn = useMemo(
    () => ({
      field: fieldMapping.endDateField,
      headerName: "End Date",
      flex: 0.8,
    }),
    [fieldMapping]
  );

  // ---------- Small API helpers using async/await ----------
  const checkAssignmentName = useCallback(
    async (name) => {
      if (!name) return { exists: false };
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/api/checkassignmentname/${encodeURIComponent(name)}`,
          {
            method: "GET",
            headers: { Authorization: "Bearer " + localStorage.getItem("token") },
          }
        );
        const data = await res.json();
        return data;
      } catch (err) {
        console.error("checkAssignmentName error:", err);
        return { exists: false }; // fail-safe: don't block assignment
      }
    },
    []
  );

  const checkLabAssessment = useCallback(async (assessmentName, labTypeValue, labId) => {
    if (!assessmentName || !labId) return { exists: false };
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/checklabassessment/${encodeURIComponent(
          assessmentName
        )}/${encodeURIComponent(labTypeValue)}/${encodeURIComponent(labId)}`,
        {
          method: "GET",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        }
      );
      const data = await res.json();
      return data;
    } catch (err) {
      console.error("checkLabAssessment error:", err);
      return { exists: false };
    }
  }, []);

  const submitAssignmentAPI = useCallback(async (payload) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/quizassign/`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      
      // Check if response is OK (status 200-299)
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        return { 
          code: res.status, 
          error: true,
          message: errorData.message || `Server error: ${res.status}` 
        };
      }
      
      // Parse response data
      const data = await res.json().catch(() => ({}));
      
      // If response has an error code or error field, treat as error
      if (data.code === 500 || data.error || data.detail) {
        return { 
          code: data.code || 500, 
          error: true,
          message: data.message || data.detail || "Failed to assign. Please try again." 
        };
      }
      
      // Success - return data (may be empty object if API doesn't return data)
      return { success: true, ...data };
    } catch (err) {
      console.error("submitAssignmentAPI error:", err);
      return { 
        code: 500, 
        error: true,
        message: "Network or server error. Please check your connection and try again." 
      };
    }
  }, [getAuthHeaders]);

  // ---------- Handlers ----------
  const handleLabSelection = useCallback(
    (labId) => {
      setSelectedLabId(labId);
      const labData = pcclabdata.find((item) => item.id === labId);
      if (labData) {
        setStartDate(labData[fieldMapping.startDateField] || "");
        setEndDate(labData[fieldMapping.endDateField] || "");
        setStopTime(labData.stop_time || "");
        setTraineeCount(labData.number_of_trainee || "");
      } else {
        // Clear derived data if none
        setStartDate("");
        setEndDate("");
        setStopTime("");
        setTraineeCount("");
      }
    },
    [pcclabdata, fieldMapping]
  );

  const handleAssessmentInput = (e) => setSelectedAssessmentName(e.target.value);
  const handleDateChange = (e) => setAssessmentDate(e.target.value);

  const handleAssignmentNameChange = (e) => {
    setAssignmentName(e.target.value);
    if (assignNameError) setAssignNameError(""); // clear immediately on change
  };

  useEffect(() => {
    // Clear assignment error when assignmentName changes (defensive)
    if (assignmentName && assignNameError) {
      setAssignNameError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentName]);

  const handleAssessmentTypeChange = (e) => setAssessmentType(e.target.value);

  const handleAssessmentCancel = () => {
    // Reset to step 1
    setCurrentStep(1);
    setAssignmentName("");
    setAssessmentDate("");
    setSelectedLabId("");
    setSelectedAssessmentName("");
    setAssessmentType("common");
    setStartDate("");
    setEndDate("");
    setPage(0);
    setAssignNameError("");
    setQuizAssignError("");
  };

  const handleContinue = () => {
    setCurrentStep((prev) => Math.min(prev + 1, 3));
    setAssessmentType("common");
  };

  // ---------- Submit flow (converted to async/await + sequential checks) ----------
  const onSubmitClick = async (e) => {
    e.preventDefault();

    // Basic validation - guard early
    if (!assignmentName) {
      setAssignNameError("Please enter assignment name.");
      return;
    }
    if (!assessmentDate) {
      setQuizAssignError("Please select an assessment date.");
      return;
    }
    if (!selectedLabId) {
      setQuizAssignError("Please select a lab first.");
      return;
    }
    if (!selectedAssessmentName) {
      setQuizAssignError("Please select an assessment first.");
      return;
    }

    setQuizAssignError("");
    setAssignNameError("");
    setSubmitting(true);

    try {
      // 1) Check assignment name
      const nameCheck = await checkAssignmentName(assignmentName);
      if (nameCheck.exists) {
        setAssignNameError("Assignment name already exists. Please enter a different name.");
        setSubmitting(false);
        return;
      }

      // 2) Check if assessment already exists for lab
      const labAssessmentCheck = await checkLabAssessment(selectedAssessmentName, labType, selectedLabId);
      if (labAssessmentCheck.exists) {
        setQuizAssignError("Assessment already exists. Please select a different assessment.");
        setSubmitting(false);
        return;
      }

      // 3) Submit assignment
      const payload = {
        assessmentDate,
        labId: selectedLabId,
        assessmentName: selectedAssessmentName,
        labType,
        assignmentName,
        labStopTime: stopTime,
        noOfTrainee: traineeCount,
        labEndDate: endDate,
      };

      const submitRes = await submitAssignmentAPI(payload);

      // Check if there's an error (code 500, error flag, or detail field)
      if (submitRes.error || submitRes.code === 500 || submitRes.detail) {
        // handle server error
        setQuizAssignError(submitRes.message || "Failed to assign. Please try again.");
        setSubmitting(false);
        return;
      }

      // Success: clear errors, show snackbar, then navigate
      setAssignNameError("");
      setQuizAssignError("");
      setSubmitting(false);
      setSubmitCompleted(true);

      // Reset form fields (clean slate)
      setAssignmentName("");
      setAssessmentDate("");
      setSelectedAssessmentName("");
      setSelectedLabId("");
      setStartDate("");
      setEndDate("");
      setStopTime("");
      setTraineeCount("");
      setCurrentStep(1);
    } catch (err) {
      console.error("submit flow error:", err);
      setQuizAssignError("Unexpected error. Please try again.");
      setSubmitting(false);
    }
  };

  // ---------- Render ----------
  return (
    <>
      <Box sx={{ width: "100%", height: "100%" }}>
        {/* STEP 1: Lab selection */}
        {currentStep === 1 && (
          <CardContent>
            <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%", mb: 1 }}>
              <SearchField
                value={searchTerm}
                placeholder="Search Lab Name"
                onChange={setSearchTerm}
                style={{ width: "100%", maxWidth: labType === "custom" ? 360 : 320 }}
              />
            </Box>

            <Grid container spacing={0}>
              <Grid item xs={12}>
                <Card>
                  {paginatedPccData.length > 0 ? (
                    <Box sx={{ width: "100%", overflow: "hidden" }}>
                      <DataGrid
                        rows={paginatedPccData}
                        columns={[
                          {
                            field: "radioButton",
                            headerName: "Select Lab",
                            minWidth: 95,
                            flex: 0.6,
                            headerAlign: "center",
                            align: "center",
                            renderCell: (params) => (
                              <Radio
                                checked={selectedLabId === params.row.id}
                                onChange={() => handleLabSelection(params.row.id)}
                              />
                            ),
                          },
                          { field: "id", headerName: "Lab ID", flex: 0.5 },
                          labNameColumn,
                          startDateColumn,
                          endDateColumn,
                          { field: "number_of_trainee", headerName: "# Trainees", flex: 0.9 },
                          {
                            field: "status",
                            headerName: "Status",
                            flex: 0.9,
                            renderCell: (params) => {
                              const colors = {
                                Running: "green",
                                Creating: "blue",
                                Stopped: "red",
                              };
                              return (
                                <span style={{ color: colors[params.value] || "black", fontWeight: "bold" }}>
                                  {params.value}
                                </span>
                              );
                            },
                          },
                        ]}
                        autoHeight
                        hideFooter
                      />

                      <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={filteredPccData.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        onRowsPerPageChange={(e) => {
                          setRowsPerPage(parseInt(e.target.value, 10));
                          setPage(0);
                        }}
                        sx={{
                          "& .MuiTablePagination-toolbar": {
                            justifyContent: "flex-start",
                            paddingLeft: 2,
                          },
                          "& .MuiTablePagination-spacer": {
                            display: "none",
                          },
                          "& .MuiTablePagination-selectLabel": {
                            margin: 0,
                          },
                          "& .MuiTablePagination-displayedRows": {
                            margin: 0,
                          },
                        }}
                      />
                    </Box>
                  ) : (
                    <Box sx={{ pt: 1, textAlign: "center" }}>
                      <Typography>No Data Available</Typography>
                    </Box>
                  )}
                </Card>
              </Grid>
            </Grid>

            <Box sx={{ textAlign: "center", mt: 2 }}>
              <Tooltip title="Cancel">
                <span>
                  <Button
                    variant="contained"
                    color="secondary"
                    disabled={!selectedLabId}
                    onClick={() => handleLabSelection(null)}
                    sx={{ mx: 1, borderRadius: "40px" }}
                  >
                    Cancel
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Continue">
                <span>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={!selectedLabId}
                    onClick={handleContinue}
                    sx={{ mx: 1, borderRadius: "40px" }}
                  >
                    Continue
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </CardContent>
        )}

        {/* STEP 2: Assessment selection */}
        {currentStep === 2 && selectedLabId && (
          <CardContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 2,
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="h6">Assessment Type</Typography>
                <FormControl sx={{ minWidth: 150 }}>
                  <Select value={assessmentType} onChange={handleAssessmentTypeChange} sx={{ height: 35 }}>
                    <MenuItem value="common">Public</MenuItem>
                    <MenuItem value="individual">Customer Owned</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <SearchField
                value={searchTerm}
                placeholder="Search Assessment Name"
                onChange={setSearchTerm}
                style={{ width: "100%", maxWidth: 250 }}
              />
            </Box>

            <DataGrid
              rows={filteredAssessments}
              getRowId={(row) => row.name}
              columns={[
                {
                  field: "select",
                  headerName: "Select Assessment",
                  flex: 1,
                  align: "center",
                  headerAlign: "center",
                  renderCell: (params) => (
                    <Radio
                      checked={selectedAssessmentName === params.row.name}
                      onChange={handleAssessmentInput}
                      value={params.row.name}
                    />
                  ),
                },
                { field: "name", headerName: "Assessment Name", flex: 1 },
                {
                  field: "organization_type",
                  headerName: "Assessment Type",
                  flex: 1,
                  renderCell: (params) => (
                    <span>
                      {params.value === "Common" ? "Public Owned Assessment" : "Customer Owned Assessment"}
                    </span>
                  ),
                },
              ]}
              autoHeight
              pagination
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[5, 10, 20]}
              sx={{
                "& .MuiDataGrid-footerContainer": {
                  justifyContent: "flex-start",
                  paddingLeft: 2,
                },
                "& .MuiDataGrid-pagination": {
                  margin: 0,
                },
              }}
            />

            <Box sx={{ textAlign: "center", mt: 2 }}>
              <Tooltip title="Cancel">
                <span>
                  <Button
                    variant="contained"
                    color="secondary"
                    disabled={!selectedAssessmentName}
                    onClick={handleAssessmentCancel}
                    sx={{ mx: 1, borderRadius: "40px" }}
                  >
                    Cancel
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Continue">
                <span>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={!selectedAssessmentName}
                    onClick={handleContinue}
                    sx={{ mx: 1, borderRadius: "40px" }}
                  >
                    Continue
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </CardContent>
        )}

        {/* STEP 3: Assignment details */}
        {currentStep === 3 && selectedAssessmentName && (
          <CardContent>
            <Typography variant="h5" sx={{ mb: 3 }}>
              Assignment Details
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="h6">Enter Assignment Name</Typography>
                <TextField
                  label="Assignment Name"
                  value={assignmentName}
                  onChange={handleAssignmentNameChange}
                  fullWidth
                  sx={{ mt: 2 }}
                />
                {assignNameError && <Typography color="error">{assignNameError}</Typography>}
              </Grid>

              <Grid item xs={6}>
                <Typography variant="h6">Choose Assessment Date</Typography>
                <Box sx={{ mt: 2 }}>
                  <input
                    type="date"
                    min={startDate || new Date().toISOString().split("T")[0]}
                    max={endDate || ""}
                    value={assessmentDate || ""}
                    onChange={handleDateChange}
                    style={{
                      width: "100%",
                      border: "none",
                      borderBottom: "1px solid black",
                      fontSize: "16px",
                      outline: "none",
                    }}
                  />
                </Box>
              </Grid>
            </Grid>

            {quizAssignError && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="error">{quizAssignError}</Alert>
              </Box>
            )}

            <Box sx={{ textAlign: "center", mt: 3 }}>
              <Tooltip title="Cancel">
                <Button variant="contained" color="secondary" onClick={handleAssessmentCancel} sx={{ mx: 1 }}>
                  Cancel
                </Button>
              </Tooltip>

              <Tooltip title={assignmentName && assessmentDate ? "Submit" : "Enter assignment name and date"}>
                <span>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={!(assignmentName && assessmentDate) || submitting}
                    onClick={onSubmitClick}
                    sx={{ mx: 1 }}
                  >
                    {submitting ? "Submitting..." : "Submit"}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </CardContent>
        )}
      </Box>

      <Snackbar
        anchorOrigin={{ vertical, horizontal }}
        open={submitCompleted}
        autoHideDuration={2000}
        onClose={() => {
          setSubmitCompleted(false);
          // Navigate to assignment list after snackbar closes
          window.location.href = "/app/assign";
        }}
      >
        <MuiAlert 
          elevation={6} 
          variant="filled" 
          sx={{ backgroundColor: "#38761d", fontSize: "18px" }} 
          severity="success"
        >
          Assignment assigned successfully!
        </MuiAlert>
      </Snackbar>
    </>
  );
};

export default GenericQuizAssign;
